import clsx from 'clsx';
import * as React from 'react';
import type { AccessibilityProps } from 'react-native';

import { Icon } from '@gitbook/spine-icons';
import { type IconDefinition, Pressable, renderIcon } from '@gitbook/spine-react';

import { Interactive } from '../Interactive';
import styles from './ListItem.module.css';

export type ListItemLayout = 'default' | 'wide';

export interface ListItemProps extends AccessibilityProps {
    icon?: IconDefinition;
    endIcon?: IconDefinition;

    /** Show the item as active/selected */
    active?: boolean;

    /**
     * Layout for the item.
     * default: padding on each side
     * wide: no side padding
     */
    layout?: ListItemLayout;

    /** First main text line */
    title: React.ReactNode;

    /** Second sub text line */
    hint?: React.ReactNode;

    /** Badges on the left side of the item */
    badges?: Array<React.ReactNode | null | false>;

    /** Where to display a delimiter line */
    delimiters?: 'bottom';

    /** When specified, a drag handle is displayed on hover */
    dragHandleRef?: React.Ref<HTMLDivElement>;

    /**
     * Callback when pressing the item.
     * If one is passed the component is interactive
     */
    onPress?: () => void;

    /** Button on the right side of the item */
    buttons?: Array<React.ReactNode | null | false>;

    /** If true, buttons are visible only on hover or active */
    buttonsOnlyWhen?: 'active' | 'hovered' | null;

    className?: string;

    style?: React.CSSProperties;

    /**
     * You can specify a `href` in addition to `onPress` for SSR and accessibility.
     */
    href?: string;
}

/**
 * An item on a list, used for settings.
 */
export function ListItem(props: ListItemProps): React.ReactElement {
    const {
        icon: IconCo,
        endIcon: EndIconCo,
        layout = 'default',
        active = false,
        title,
        hint,
        badges = [],
        dragHandleRef,
        onPress,
        buttons = [],
        buttonsOnlyWhen = null,
        className,
        delimiters,
        ...rest
    } = props;
    const interactiveState = Interactive.useInteractiveState();

    const showButtons = (() => {
        if (buttonsOnlyWhen === null) {
            return true;
        }

        if (buttonsOnlyWhen === 'active') {
            return active || interactiveState.active;
        }

        if (buttonsOnlyWhen === 'hovered') {
            return interactiveState.hovered;
        }
    })();

    return (
        <Pressable onPress={onPress}>
            <div
                {...rest}
                {...interactiveState.domEvents}
                {...(onPress ? { role: 'button', tabIndex: 0 } : {})}
                className={clsx(
                    'group flex items-center py-2',
                    layout === 'default' && 'px-6',
                    delimiters === 'bottom' && 'border-b',
                    styles.listItem,
                    onPress && 'cursor-pointer focus-visible:shadow-focus-ring',
                    onPress && styles.interactive,
                    className
                )}
            >
                {dragHandleRef ? (
                    <div
                        ref={dragHandleRef}
                        className="absolute left-1 flex cursor-grab opacity-0 transition group-hover:opacity-100"
                    >
                        <Icon.GrabberVertical size={16} />
                    </div>
                ) : null}
                {IconCo ? renderIcon(IconCo, { size: 32, className: 'mr-4' }) : null}
                <div className="flex flex-1 items-center">
                    <div className="flex-1">
                        <div className="text-muted">{title}</div>
                        {hint ? <div className="text-muted text-ui-small">{hint}</div> : null}
                    </div>
                    {badges.length > 0 ? (
                        <div className="flex items-center gap-2">
                            {badges.map((badge, index) =>
                                badge ? <React.Fragment key={index}>{badge}</React.Fragment> : null
                            )}
                        </div>
                    ) : null}
                </div>
                <div
                    className={clsx(
                        'flex items-center gap-2',
                        showButtons ? 'opacity-100' : 'opacity-0'
                    )}
                >
                    {buttons.map((button, index) =>
                        button ? <React.Fragment key={index}>{button}</React.Fragment> : null
                    )}
                </div>
                {EndIconCo ? renderIcon(EndIconCo, { size: 32, className: 'mr-2' }) : null}
            </div>
        </Pressable>
    );
}
